import React from "react";
import {
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  pdf,
  Document,
} from "@react-pdf/renderer";
import { isLineDateOnly } from "../../utils/appointmentTimes";

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 9,
    lineHeight: 1.4,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  logo: {
    height: 75,
    width: 150,
  },
  practiceName: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 4,
  },
  title: {
    textAlign: "center",
    fontSize: 12,
    fontWeight: "bold",
    marginVertical: 8,
    textTransform: "uppercase",
  },
  heading: {
    fontSize: 11,
    fontWeight: "bold",
    marginVertical: 6,
    marginLeft: 10,
  },
  heading2: {
    marginLeft: 20,
  },
  heading3: {
    fontSize: 11,
    fontWeight: "bold",
    marginVertical: 6,
    textAlign: "center",
    marginBottom: 1,
    marginTop: 1,
  },
  section: {
    marginBottom: 10,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginRight: 50,
  },
  text: {
    fontSize: 9,
  },
  textSmall: {
    fontSize: 8,
  },
  tableHeader: {
    flexDirection: "row",
    borderBottom: 2,
    borderBottomColor: "#000",
    paddingBottom: 1,
    marginTop: 2,
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    marginBottom: 2,
  },
  tableRowHeading: {
    flexDirection: "row",
    fontWeight: "bold",
  },
  cell: {
    width: "16.5%",
    paddingRight: 4,
    fontWeight: "bold",
  },
  colDate: {
    width: "12%",
    paddingRight: 4,
  },
  colDescription: {
    width: "38%",
    paddingRight: 4,
  },
  colQty: {
    width: "10%",
    paddingRight: 4,
  },
  colInvoiced: {
    width: "10%",
    paddingRight: 4,
    textAlign: "right",
  },
  colPatient: {
    width: "10%",
    paddingRight: 4,
    textAlign: "right",
  },
  colMedicalAid: {
    width: "10%",
    paddingRight: 4,
    textAlign: "right",
  },
  colBalance: {
    width: "10%",
    paddingRight: 4,
    textAlign: "right",
  },
  colTariff: {
    width: "50%",
    paddingRight: 5,
  },
  bold: {
    fontWeight: "bold",
  },
  indent: {
    marginLeft: 10,
    fontSize: 8,
  },
  line: {
    height: 1,
    backgroundColor: "#000",
    marginVertical: 8,
  },
  receiptLine: {
    fontSize: 10,
  },
  services: {
    fontSize: 8,
  },
  balanceContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 10,
    marginBottom: 10,
    borderBottom: 0,
    borderBottomColor: "#000",
    paddingRight: 3,
    paddingLeft: 3,
  },
  icdText: {
    fontSize: 7,
    marginTop: 2,
    color: "#555",
  },

  tableRowClosing: {
    flexDirection: "row",
    marginBottom: 0,
    borderBottom: 2,
  },
  // Aged balances table
  agedBalancesContainer: {
    marginTop: 10,
    width: "60%",
    marginBottom: 10,
    borderBottom: 1,
    borderTop: 1,
    borderLeft: 1,
    borderRight: 1,
    alignSelf: "flex-end",
    marginLeft: "auto",
    padding: 4,
  },
  agedBalancesTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 5,
    textAlign: "center",
  },
  agedBalancesTable: {
    width: "100%",
  },
  agedBalancesHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingBottom: 3,
    paddingTop: 3,
    backgroundColor: "#f5f5f5",
  },
  agedBalancesRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#ddd",
    paddingBottom: 3,
    paddingTop: 3,
  },
  agedBalancesCell: {
    flex: 1,
    textAlign: "center",
    fontSize: 9,
    paddingHorizontal: 2,
  },
  agedBalancesCellFirst: {
    flex: 1.2,
    textAlign: "left",
    paddingLeft: 5,
    fontWeight: "bold",
  },
  italics: {
    fontStyle: "italic",
  },
  messageContainer: {
    marginTop: 8,
    marginBottom: 8,
    padding: 8,
    borderWidth: 1,
    borderColor: "#000",
  },
  messageHeading: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 4,
  },
  messageText: {
    fontSize: 9,
    lineHeight: 1.3,
  },
  spacerRow: {
  height: 10, // adjust to taste (8–14 works well)
},
});

// ---------- helpers ----------

// turn "F82;F83" or ["F82","F83"] into ["F82","F83"]
const normalizeCodes = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v.map(safeTextExtract).filter(Boolean);
  const s = safeTextExtract(v);
  if (!s) return [];
  return s
    .split(/[;,]\s*/)
    .map((x) => x.trim())
    .filter(Boolean);
};

const formatModifiers = (modifier) => {
  if (modifier == null) return "N/A";
  const out = [];
  const pushCode = (m) => {
    if (m == null) return;
    if (typeof m === "string" || typeof m === "number") {
      out.push(String(m));
    } else if (Array.isArray(m)) {
      m.forEach(pushCode);
    } else if (typeof m === "object") {
      if (m.code) out.push(String(m.code));
      else if (m.mod) out.push(String(m.mod));
      else if (m.value) out.push(String(m.value));
      else if (m.id) out.push(String(m.id));
    }
  };
  pushCode(modifier);
  return out.length ? out.join(", ") : "N/A";
};

const safeTextExtract = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return value.toString();
  if (typeof value === "object") {
    if (value.code) return value.code;
    if (value.description) return value.description;
    if (value.name) return value.name;
    return "";
  }
  return String(value);
};

// Look up PMB auth number for an ICD10 code from the invoice-level icd10Codes array
const formatIcdWithPmb = (code, icd10Codes) => {
  if (!code || !icd10Codes?.length) return code || "";
  const codeStr = typeof code === "string" ? code : safeTextExtract(code);
  const match = icd10Codes.find(
    (c) => c.ICD10_Code === codeStr && c.isPMB && c.pmbAuthNo
  );
  return match ? `${codeStr} PMB (Auth: ${match.pmbAuthNo})` : codeStr;
};

// Extract HH:mm robustly from a date-time string
const extractHHMM = (dt) => {
  if (!dt || typeof dt !== "string") return "";
  const m = dt.match(/T(\d{2}:\d{2})/); // ISO-like
  if (m) return m[1];
  const m2 = dt.match(/\b(\d{2}:\d{2})\b/); // any HH:mm inside
  if (m2) return m2[1];
  const tail = dt.slice(-5);
  return /^\d:\d{2}$/.test(tail) ? `0${tail}` : tail; // very last fallback
};

// Pick the line date (prefer custom date if present), and attach time if available.
// Date-only lines (no times) suppress the time portion.
const pickLineDate = (s) => {
  const dateCustom = s.appointmentCustom || s.appointmentDateCustom || "";
  const dateSet = s.appointmentDateSet || "";
  // If we only have a start datetime, derive the date from it
  const fallbackFromStart =
    (s.appointmentStartSet && s.appointmentStartSet.slice(0, 10)) ||
    (s.appointmentCustomStart && s.appointmentCustomStart.slice(0, 10)) ||
    "";
  const datePart =
    (dateCustom && String(dateCustom)) ||
    (dateSet && String(dateSet)) ||
    fallbackFromStart;

  const timePart = isLineDateOnly(s)
    ? ""
    : extractHHMM(s.appointmentCustomStart) || extractHHMM(s.appointmentStartSet);

  return datePart ? `${datePart}${timePart ? ` ${timePart}` : ""}` : "";
};

// ---------- components ----------

const AgedBalancesTable = ({ agedBalances, account }) => {
  return (
    <View style={styles.agedBalancesContainer}>
      <View style={styles.agedBalancesTable}>
        <View style={styles.agedBalancesHeader}>
          <Text style={styles.agedBalancesCellFirst}>Outstanding</Text>
          <Text style={styles.agedBalancesCell}>30 Days</Text>
          <Text style={styles.agedBalancesCell}>60 Days</Text>
          <Text style={styles.agedBalancesCell}>90 Days</Text>
          <Text style={styles.agedBalancesCell}>120+ Days</Text>
        </View>
        <View style={styles.agedBalancesRow}>
          <Text style={styles.agedBalancesCellFirst}>Patient</Text>
          <Text style={styles.agedBalancesCell}>
            R {(agedBalances?.days30?.patient || 0).toFixed(2)}
          </Text>
          <Text style={styles.agedBalancesCell}>
            R {(agedBalances?.days60?.patient || 0).toFixed(2)}
          </Text>
          <Text style={styles.agedBalancesCell}>
            R {(agedBalances?.days90?.patient || 0).toFixed(2)}
          </Text>
          <Text style={styles.agedBalancesCell}>
            R {(agedBalances?.days120plus?.patient || 0).toFixed(2)}
          </Text>
        </View>
        <View style={styles.agedBalancesRow}>
          <Text style={styles.agedBalancesCellFirst}>Medical Aid</Text>
          <Text style={styles.agedBalancesCell}>
            R {(agedBalances?.days30?.medicalAid || 0).toFixed(2)}
          </Text>
          <Text style={styles.agedBalancesCell}>
            R {(agedBalances?.days60?.medicalAid || 0).toFixed(2)}
          </Text>
          <Text style={styles.agedBalancesCell}>
            R {(agedBalances?.days90?.medicalAid || 0).toFixed(2)}
          </Text>
          <Text style={styles.agedBalancesCell}>
            R {(agedBalances?.days120plus?.medicalAid || 0).toFixed(2)}
          </Text>
        </View>
        <View style={styles.agedBalancesRow}>
          <Text style={styles.agedBalancesCellFirst}>Unallocated:</Text>
          <Text style={styles.agedBalancesCell}>
            (R {(account.totalUnallocated || 0).toFixed(2)})
          </Text>
          <Text style={styles.agedBalancesCell}></Text>
          <Text style={styles.agedBalancesCell}></Text>
          <Text style={styles.agedBalancesCell}></Text>
        </View>
      </View>
    </View>
  );
};

const PracticeStatementPage = ({
  practice,
  transactions,
  account,
  dateRange,
  openingBalance,
  closingBalance,
  isFirstPractice,
  isLastPractice,
  isFirstPage,
  isLastPage,
  agedBalances,
  banking,
  invoicesByPractice,
  notesForStatement,
  unallocatedReceipts = [],
}) => {
  const isVatRegistered = !!practice?.vatNumber;

  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        {practice?.logoURL && (
          <Image
            style={[styles.logo, { maxHeight: 150, width: "auto" }]}
            src={practice.logoURL}
          />
        )}
        <View>
          <Text style={styles.practiceName}>
            {practice?.practiceName || ""}
          </Text>

          {/* Practice Number */}
          <Text>
            <Text style={styles.bold}>Practice No: </Text>
            <Text>{practice?.practiceNumber || ""}</Text>
          </Text>
          <Text>
            <Text style={styles.bold}>HPCSA No: </Text>
            <Text>{practice?.hpcsaNumber || "N/A"}</Text>
          </Text>

          {/* VAT */}
          {isVatRegistered && (
            <Text>
              <Text style={styles.bold}>VAT: </Text>
              <Text>{practice?.vatNumber || ""}</Text>
            </Text>
          )}

          {/* Horizontal Line */}
          <View style={styles.line} />

          {/* Address */}
          <Text>
            {practice?.practiceAddress || ""}
            {practice?.practiceAddress2 ? `, ${practice.practiceAddress2}` : ""}
          </Text>
          <Text>
            {practice?.practiceCity || ""}
            {practice?.practicePostalCode
              ? `, ${practice.practicePostalCode}`
              : ""}
          </Text>

          {/* Email */}
          <Text>
            <Text style={styles.bold}>Email: </Text>
            <Text>{practice?.practiceEmail || ""}</Text>
          </Text>

          {/* Telephone */}
          <Text>
            <Text style={styles.bold}>Tel: </Text>
            <Text>{practice?.practicePhoneNumber || ""}</Text>
          </Text>
        </View>
      </View>
      <View style={styles.line} />
      <Text style={styles.heading3}>
        STATEMENT OF ACCOUNT - {dateRange.end}
      </Text>
      <View style={styles.line} />
      <View style={styles.row}>
        <View>
          <Text style={[styles.text, styles.bold]}>
            {account?.accountFullNames || ""} {account?.accountLastName || ""}
          </Text>
          <Text>
            <Text style={styles.bold}>ACC #: </Text>
            <Text>{account?.accountNumber || ""}</Text>
          </Text>
          <Text style={styles.text}>
            {account?.accountHomeAddress1 || ""}
            {account?.accountHomeAddress2
              ? `, ${account.accountHomeAddress2}`
              : ""}
          </Text>
          <Text style={styles.text}>
            {account?.accountHomeAddressCity || ""}
            {account?.accountHomeAddressPostalCode
              ? `, ${account.accountHomeAddressPostalCode}`
              : ""}
          </Text>
          <Text style={styles.text}></Text>
          <Text style={styles.text}>
            {account?.accountPhoneNumberCell ||
              account?.accountTelNumberCell ||
              ""}
          </Text>
          <Text style={[styles.text, styles.section]}>
            {account?.accountEmail || ""}
          </Text>
        </View>
        <View>
          {account?.accountMedicalAid ? (
            <>
              <Text style={styles.text}>{account.accountMedicalAid}</Text>

              {(account.accountMedicalAidPlan ||
                account.accountMedicalAidOption) && (
                <Text style={styles.text}>
                  {account.accountMedicalAidPlan || ""}
                  {account.accountMedicalAidPlan &&
                    account.accountMedicalAidOption &&
                    " | "}
                  {account.accountMedicalAidOption || ""}
                </Text>
              )}

              <Text style={styles.text}>
                {account.accountMedicalAidNumber || ""}
              </Text>
            </>
          ) : account?.accountIODFund ? (
  <>
    <Text style={styles.text}>
      <Text>{account.accountIODFund}</Text>
    </Text>

    {(account.accountIODPlan || account.accountIODOption) && (
      <Text style={styles.text}>
        <Text>{account.accountIODPlan || ""}</Text>
        {account.accountIODPlan && account.accountIODOption ? " | " : ""}
        <Text>{account.accountIODOption || ""}</Text>
      </Text>
    )}

    {account.accountIODClaimRefNumber && (
      <Text style={styles.text}>
        <Text style={styles.bold}>Claim Ref: </Text>
        <Text>{account.accountIODClaimRefNumber}</Text>
      </Text>
    )}

    {account.accountIODEmployer && (
      <Text style={styles.text}>
        <Text style={styles.bold}>Employer: </Text>
        <Text>{account.accountIODEmployer}</Text>
      </Text>
    )}

    {account.accountIODEmployerWCARegNum && (
      <Text style={styles.text}>
        <Text style={styles.bold}>WCA Reg #: </Text>
        <Text>{account.accountIODEmployerWCARegNum}</Text>
      </Text>
    )}

    {account.accountIODDateOfInjury && (
      <Text style={styles.text}>
        <Text style={styles.bold}>Date of Injury: </Text>
        <Text>{account.accountIODDateOfInjury}</Text>
      </Text>
    )}
    <View style={styles.spacerRow} />
  </>

          ) : (
            <Text style={styles.text}>Private Patient</Text>
          )}
        </View>
      </View>

      <View style={styles.tableHeader}>
        <Text style={styles.colDate}>Date</Text>
        <Text style={styles.colDescription}>Description</Text>
        <Text style={styles.colQty}>Modifier</Text>
        <Text style={styles.colInvoiced}>Invoiced</Text>
        <Text style={styles.colPatient}>Patient</Text>
        <Text style={styles.colMedicalAid}>Med Aid</Text>
        <Text style={styles.colBalance}>Balance</Text>
      </View>

      {/* Opening balance only on very first page of first practice */}
      {isFirstPractice && isFirstPage && (
        <View style={styles.tableRow}>
          <Text style={[styles.colDate, styles.bold]}>{dateRange.start}</Text>
          <Text style={[styles.colDescription, styles.bold]}>
            Opening Balance
          </Text>
          <Text style={[styles.colQty, styles.bold]}></Text>
          <Text style={[styles.colInvoiced, styles.bold]}></Text>
          <Text style={styles.colPatient}></Text>
          <Text style={styles.colMedicalAid}></Text>
          <Text style={[styles.colBalance, styles.bold]}>
            R {openingBalance.toFixed(2)}
          </Text>
        </View>
      )}

      {transactions.map((t, i) => (
        <View key={i} wrap={false} minPresenceAhead={100}>
          {/* ── Invoice header row (date, "Invoice: ####", amounts) ── */}
          <View style={styles.tableRow}>
            <Text style={[styles.colDate, styles.bold]}>{t.invoiceDate}</Text>
            <Text style={[styles.colDescription, styles.bold]}>
              Invoice: {t.invoiceNumber}
            </Text>
            <Text style={[styles.colQty, styles.bold]}></Text>
            <Text style={[styles.colInvoiced, styles.bold]}>
              R {t.totalAmount?.toFixed(2)}
            </Text>
            <Text style={styles.colPatient}></Text>
            <Text style={styles.colMedicalAid}></Text>
            <Text style={[styles.colBalance, styles.bold]}>
              R {t.runningBalance?.toFixed(2)}
            </Text>
          </View>

          {/* ── Case info + Practitioner row(s) ──
              Layout depends on what's present (matches ExportInvoicePDF):
                - Both referring doctor AND auth → two rows
                    Row 1 (italic): "<ref sentence> | Authorization X applies"
                    Row 2: "Place of Service: … Practitioner: …"
                - Only one of ref/auth → single row combining it with practitioner
                - Neither → practitioner row only
              ICD10 codes (when present) ride with the referring sentence as
              "with the following X, Y" — same wording as before. */}
          {(() => {
            const refParts = [];
            if (t.case?.referringDoctor) {
              let by = `The patient was referred by ${t.case.referringDoctor}`;
              if (t.case.referringDoctorBHF) by += ` (BHF: ${t.case.referringDoctorBHF})`;
              refParts.push(by);
            }
            const codes = [
              ...normalizeCodes(t.case?.icd101),
              ...normalizeCodes(t.case?.icd102),
            ];
            if (codes.length) {
              refParts.push(`with the following ${codes.join(", ")}`);
            }
            const refSentence  = refParts.join(" ");
            const authSentence = t.case?.caseAuthNumber
              ? `Authorization ${t.case.caseAuthNumber} applies`
              : "";
            const practitionerStr =
              `Place of Service: ${t.placeOfService || ""} Practitioner: ` +
              `${t.practitioner?.firstName || ""} ${t.practitioner?.lastName || ""} ` +
              `HPCSA: ${t.practitioner?.hpcsa || ""}, Practice: ${t.practiceNumber || ""}`;

            const hasRef  = !!refSentence;
            const hasAuth = !!authSentence;

            const infoRow = (text, italic) => (
              <View style={styles.tableRow}>
                <Text style={styles.colDate}></Text>
                <Text
                  style={
                    italic
                      ? [styles.colDescription, styles.textSmall, styles.italics]
                      : [styles.colDescription, styles.textSmall]
                  }
                >
                  {text}
                </Text>
                <Text style={styles.colQty}></Text>
                <Text style={styles.colInvoiced}></Text>
                <Text style={styles.colPatient}></Text>
                <Text style={styles.colMedicalAid}></Text>
                <Text style={styles.colBalance}></Text>
              </View>
            );

            if (hasRef && hasAuth) {
              return (
                <>
                  {infoRow(`${refSentence} | ${authSentence}`, true)}
                  {infoRow(practitionerStr, false)}
                </>
              );
            }
            if (hasRef) {
              return infoRow(`${refSentence} | ${practitionerStr}`, false);
            }
            if (hasAuth) {
              return infoRow(`${authSentence} | ${practitionerStr}`, false);
            }
            return infoRow(practitionerStr, false);
          })()}

          {t.patients?.map((p, pi) => (
            <View key={pi}>
              <Text style={[styles.textSmall, styles.bold]}>
                ({p.patientMedicalAidDependantCode || "N/A"}){" "}
                {p.patientFirstName || ""} {p.patientLastName || ""}
              </Text>
              {p.services?.map((s, si) => (
                <View key={si} style={styles.services}>
                  <View style={styles.tableRow} wrap={true}>
                    <View style={styles.colTariff}>
                      <Text style={styles.bold}>{pickLineDate(s)} </Text>
                      <Text>
                        {s.tariffCode || s.nappiCode || ""}:{" "}
                        {s.tariffDescription || ""}
                        {(s.icd101 || s.icd102) && (
                          <Text style={styles.icdText}>
                            {" "}
                            | ICD 10: {formatIcdWithPmb(s.icd101, t.icd10Codes)}
                            {s.icd102 ? ` ${formatIcdWithPmb(s.icd102, t.icd10Codes)}` : ""}
                          </Text>
                        )}
                      </Text>
                      {((s.warnings?.length || 0) > 0 ||
                        (s.rejections?.length || 0) > 0) && (
                        <Text style={styles.italics}>
                          Medical Aid Response Code(s):{" "}
                          {[...(s.warnings || [])]
                            .map((w) => safeTextExtract(w.code))
                            .join(", ")}{" "}
                          {[...(s.rejections || [])]
                            .map((r) => safeTextExtract(r.code))
                            .join(", ")}
                        </Text>
                      )}
                    </View>

                    <Text style={styles.colQty}>
                      {formatModifiers(s.modifier)}
                    </Text>

                    <Text style={styles.colInvoiced}>
                      R {(s.rate || 0).toFixed(2)}
                    </Text>
                    <Text style={styles.colPatient}></Text>
                    <Text style={styles.colMedicalAid}></Text>
                    <Text style={styles.colBalance}></Text>
                  </View>
                </View>
              ))}
            </View>
          ))}

          {t.payments?.map((p, pi) => {
            const num = p.receiptNumber || "";
            const isRefund = num.includes("RF");
            const label = num.includes("RCT")
              ? "Receipt"
              : num.includes("CN")
              ? "Credit Note"
              : isRefund
              ? "Refund"
              : "";

            const amt = parseFloat(p.paymentAmount || 0);
            const noParens = `R ${amt.toFixed(2)}`;
            const parens = `(R ${amt.toFixed(2)})`;

            return (
              <View key={`pay-${pi}`} style={styles.receiptLine}>
                <View style={styles.tableRow}>
                  <Text style={styles.colDate} />

                  <Text style={[styles.colDescription, styles.text]}>
                    {label}: {p.receiptNumber} ({p.receiptDate})
                  </Text>

                  <Text style={styles.colQty} />

                  <Text style={[styles.colInvoiced, styles.text]}>
                    {isRefund
                      ? noParens
                      : p.receiptAmount != null
                      ? parens
                      : ""}
                  </Text>

                  <Text style={[styles.colPatient, styles.text]}>
                    {!isRefund &&
                      (p.paymentSource === "Patient" ||
                      p.paymentSource === "patient" ||
                      p.paymentMethod === "Cash"
                        ? parens
                        : "")}
                  </Text>

                  <Text style={[styles.colMedicalAid, styles.text]}>
                    {!isRefund &&
                      (p.paymentSource === "Medical Aid" ||
                      p.paymentSource === "medicalAid"
                        ? parens
                        : "")}
                  </Text>

                  <Text style={styles.colBalance} />
                </View>
              </View>
            );
          })}

          <View style={styles.line} />
        </View>
      ))}

      {/* Closing balance only on very last page of last practice */}
      {isLastPractice && isLastPage && (
        <>
          <View style={styles.tableRowClosing}>
            <Text style={[styles.colDate, styles.bold]}>{dateRange.end}</Text>
            <Text style={[styles.colDescription, styles.bold]}>
              Closing Balance
            </Text>
            <Text style={[styles.colQty, styles.bold]}></Text>
            <Text style={[styles.colInvoiced, styles.bold]}></Text>
            <Text style={styles.colPatient}></Text>
            <Text style={styles.colMedicalAid}></Text>
            <Text style={[styles.colBalance, styles.bold]}>
              R {closingBalance.toFixed(2)}
            </Text>
          </View>

          <View style={styles.line} />

          {unallocatedReceipts.length > 0 && (
            <View style={{ marginTop: 10 }}>
              <Text style={[styles.bold, { fontSize: 8, textAlign: "left", marginBottom: 4 }]}>
                Unallocated Receipts
              </Text>
              <View style={[styles.tableHeader, { borderBottom: 0.5 }]}>
                <Text style={styles.colDate}>Date</Text>
                <Text style={styles.colDescription}>Description</Text>
                <Text style={styles.colQty}>Status</Text>
                <Text style={styles.colInvoiced}>Receipt</Text>
                <Text style={styles.colPatient}>Patient</Text>
                <Text style={styles.colMedicalAid}>Med Aid</Text>
                <Text style={styles.colBalance}>Balance</Text>
              </View>
              {(() => {
                let runningTotal = 0;
                return unallocatedReceipts.map((r, idx) => {
                  runningTotal += r.paymentAmount || 0;
                  const isPatient = ["patient", "Patient"].includes(r.paymentSource);
                  const isLast = idx === unallocatedReceipts.length - 1;
                  const rDate = r.receiptDate
                    ? new Date(r.receiptDate).toISOString().split("T")[0]
                    : "";
                  return (
                    <View key={`unalloc-${idx}`} style={[styles.tableRow, isLast && { borderBottom: 2, borderBottomColor: "#000", paddingBottom: 2 }]}>
                      <Text style={styles.colDate}>{rDate}</Text>
                      <Text style={styles.colDescription}>{r.receiptNumber || ""}</Text>
                      <Text style={styles.colQty}>Paid</Text>
                      <Text style={styles.colInvoiced}>
                        R {(r.paymentAmount || 0).toFixed(2)}
                      </Text>
                      <Text style={styles.colPatient}>
                        {isPatient ? `R ${(r.paymentAmount || 0).toFixed(2)}` : ""}
                      </Text>
                      <Text style={styles.colMedicalAid}>
                        {!isPatient ? `R ${(r.paymentAmount || 0).toFixed(2)}` : ""}
                      </Text>
                      <Text style={[styles.colBalance, styles.bold]}>
                        R {runningTotal.toFixed(2)}
                      </Text>
                    </View>
                  );
                });
              })()}
            </View>
          )}

          <AgedBalancesTable agedBalances={agedBalances} account={account} />
          <View style={styles.line} />

          {/* Practice Message (only on very last page of the statement) */}
          {isLastPractice &&
            isLastPage &&
            (() => {
              const noteLines = (notesForStatement || [])
                .map((n) => (n?.note || "").trim())
                .filter(Boolean);

              if (noteLines.length === 0) return null;

              return (
                <View style={styles.messageContainer}>
                  <Text style={styles.messageHeading}>Practice Message:</Text>
                  {noteLines.map((line, idx) => (
                    <Text key={`pm-${idx}`} style={styles.messageText}>
                      • {line}
                    </Text>
                  ))}
                </View>
              );
            })()}

          {/* Medical Aid Response Codes */}
          {(() => {
            const warningsMap = new Map();
            const rejectionsMap = new Map();

            invoicesByPractice.forEach((pb) =>
              (pb.invoices || []).forEach((inv) =>
                (inv.patients || []).forEach((p) =>
                  (p.services || []).forEach((s) => {
                    (s.warnings || []).forEach((w) =>
                      warningsMap.set(
                        safeTextExtract(w.code),
                        safeTextExtract(w.description)
                      )
                    );
                    (s.rejections || []).forEach((r) =>
                      rejectionsMap.set(
                        safeTextExtract(r.code),
                        safeTextExtract(r.description)
                      )
                    );
                  })
                )
              )
            );

            if (warningsMap.size === 0 && rejectionsMap.size === 0) {
              return null;
            }

            return (
              <View
                style={{
                  marginVertical: 6,
                  padding: 8,
                  borderWidth: 1,
                  borderColor: "#000",
                }}
              >
                <Text style={[styles.heading2, styles.bold]}>
                  Medical Aid Response Codes
                </Text>

                {[...warningsMap.entries()].map(([code, desc]) => (
                  <Text key={`w-${code}`} style={styles.textSmall}>
                    <Text style={styles.bold}>{code}:</Text> {desc}
                  </Text>
                ))}
                {[...rejectionsMap.entries()].map(([code, desc]) => (
                  <Text key={`r-${code}`} style={styles.textSmall}>
                    <Text style={styles.bold}>{code}:</Text> {desc}
                  </Text>
                ))}
              </View>
            );
          })()}
          {/* Payment Instructions & Banking */}
          <View style={{ marginTop: 2 }}>
            <Text style={[styles.text, { marginBottom: 4 }]}>
              The patient outstanding balances can be directly paid to the
              following account(s), please use your account number{" "}
              <Text style={styles.bold}>{account?.accountNumber}</Text> as
              reference:
            </Text>

            {banking
              ?.reduce((rows, b, idx) => {
                if (idx % 2 === 0) rows.push([b]);
                else rows[rows.length - 1].push(b);
                return rows;
              }, [])
              .map((pair, rowIdx) => (
                <View
                  key={rowIdx}
                  style={{ flexDirection: "row", marginBottom: 8 }}
                >
                  {pair.map((b, colIdx) => (
                    <View key={colIdx} style={{ flex: 1, paddingLeft: 6 }}>
                      <Text style={styles.text}>
                        <Text style={styles.bold}>Bank: </Text>
                        {b.bank?.bankName || "N/A"}
                      </Text>
                      <Text style={styles.text}>
                        <Text style={styles.bold}>Account Name: </Text>
                        {b.bank?.accountName || "N/A"}
                      </Text>
                      <Text style={styles.text}>
                        <Text style={styles.bold}>Account Number: </Text>
                        {b.bank?.accountNumber || "N/A"}
                      </Text>
                      <Text style={styles.text}>
                        <Text style={styles.bold}>Branch Code: </Text>
                        {b.bank?.branchCode || "N/A"}
                      </Text>
                      <Text style={styles.text}>
                        <Text style={styles.bold}>Account Type: </Text>
                        {b.bank?.accountType || "N/A"}
                      </Text>
                    </View>
                  ))}
                  {pair.length === 1 && <View style={{ flex: 1 }} />}
                </View>
              ))}
          </View>
        </>
      )}
    </Page>
  );
};

// Estimate rows for pagination
function chunkInvoicesByRowLimit(invoices, maxRowsPerPage = 50) {
  const chunks = [];
  let currentChunk = [];
  let currentRowCount = 0;
  for (const inv of invoices) {
    const numPatients = inv.patients?.length || 0;
    const numServices =
      inv.patients?.reduce((acc, p) => acc + (p.services?.length || 0), 0) || 0;
    const numReceipts = inv.payments?.length || 0;
    const hasCase = inv.case && (inv.case.referringDoctor || inv.case.icd101 || inv.case.icd102);
    const hasAuth = inv.case?.caseAuthNumber;
    // More accurate row estimation: header(1) + practitioner(1) + referrer(1 if exists) + auth(1 if exists) + patients + services + receipts + separator(1)
    const rowsNeeded = 3 + numPatients + numServices + numReceipts + (hasCase ? 1 : 0) + (hasAuth ? 1 : 0);

    // If this single invoice is too big, put it on its own page
    if (rowsNeeded > maxRowsPerPage) {
      if (currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentRowCount = 0;
      }
      chunks.push([inv]);
      continue;
    }

    if (currentRowCount + rowsNeeded > maxRowsPerPage && currentChunk.length > 0) {
      chunks.push(currentChunk);
      currentChunk = [];
      currentRowCount = 0;
    }
    currentChunk.push(inv);
    currentRowCount += rowsNeeded;
  }
  if (currentChunk.length > 0) chunks.push(currentChunk);
  return chunks;
}

// Exported PDF generator
export default function handleExportPDF(statementData) {
  const {
    account,
    invoicesByPractice,
    openingBalance,
    closingBalance,
    agedBalances,
    banking,
    notesForStatement,
    unallocatedReceipts = [],
  } = statementData;

  const allInvoices = invoicesByPractice.flatMap((p) => p.invoices || []);
  const sortedDates = allInvoices
    .map((inv) => new Date(inv.invoiceDate))
    .sort((a, b) => a - b);

  const dateRange = {
    start: sortedDates[0]?.toISOString().split("T")[0] || "Start",
    end: sortedDates.at(-1)?.toISOString().split("T")[0] || "End",
  };

  return async ({ asBlob = false } = {}) => {
    try {
      const pdfDoc = pdf(
        <Document>
          {invoicesByPractice.map((practiceBlock, practiceIndex) => {
            const sortedInvoices = [...(practiceBlock.invoices || [])].sort(
              (a, b) => new Date(a.invoiceDate) - new Date(b.invoiceDate)
            );
            const chunks = chunkInvoicesByRowLimit(sortedInvoices, 55);

            const isFirstPractice = practiceIndex === 0;
            const isLastPractice =
              practiceIndex === invoicesByPractice.length - 1;

            return chunks.map((chunk, i) => (
              <PracticeStatementPage
                key={`practice-${practiceIndex}-page-${i}`}
                practice={practiceBlock}
                transactions={chunk}
                account={account}
                dateRange={dateRange}
                openingBalance={openingBalance}
                closingBalance={closingBalance}
                pageIndex={i}
                totalPages={chunks.length}
                isFirstPractice={isFirstPractice}
                isLastPractice={isLastPractice}
                isFirstPage={i === 0}
                isLastPage={i === chunks.length - 1}
                agedBalances={agedBalances}
                banking={banking}
                invoicesByPractice={invoicesByPractice}
                notesForStatement={notesForStatement}
                unallocatedReceipts={unallocatedReceipts}
              />
            ));
          })}
        </Document>
      );

      const blob = await pdfDoc.toBlob();

      if (asBlob) return blob;

      const blobURL = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobURL;
      link.download = `Statement_${
        account?.accountFullNames || account?.accountName || "Account"
      }_${dateRange.start}_to_${dateRange.end}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobURL);

      return true;
    } catch (error) {
      console.error("Error generating PDF:", error);
      return false;
    }
  };
}
